import { getDatabase, ref, set, update, push, get } from "firebase/database";

import { useAuth } from "../contexts/AuthContext";
import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Cropper from "react-cropper";
import "cropperjs/dist/cropper.css";
import {
  Button,
  Form,
  Modal,
  ProgressBar,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import {
  getAllOwnerSelectionQuestions,
  getAllOwnerTextQuestions,
  getPlatformPercentages,
  newUserAddedAdminEmailBody,
  sendEmail,
  sendEmailWithRetry,
  sendSMS,
  welcomeOwnerEmailBody,
  getAllReferralCodesWithOwners,
  getOwnerMeta,
  proMemberStatusBody,
  becomeProMemberRequestAdminEmailBody,
  calculateAgeFromDateOfBirth,
} from "./utils";
import { notifyNewBoatOwner } from "./utils/adminNotifications";

const isTaxIdentifierQuestion = (question = "") => {
  const normalizedQuestion = question.trim().toLowerCase();

  return (
    normalizedQuestion.includes("social security") ||
    normalizedQuestion.includes("ssn") ||
    normalizedQuestion.includes("ein") ||
    normalizedQuestion.includes("tax id") ||
    normalizedQuestion.includes("tax identification") ||
    normalizedQuestion.includes("taxpayer identification") ||
    normalizedQuestion.includes("xxx-xx-xxxx")
  );
};

const SignupReferral = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(1);
  const database = getDatabase();
  const cropperRef = useRef(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [ownerMeta, setOwnerMeta] = useState([]);

  const [isModalOpen, setModalOpen] = useState(false);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  useEffect(() => {
    console.log(croppedImage);
  }, [croppedImage]);
  const serverUrl = process.env.REACT_APP_BASE_URL;

  const queryParams = new URLSearchParams(location.search);
  const refParam = queryParams.get("ref");

  const { signup, refreshUserData, setIsSigningUp } = useAuth();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [phoneNo, setPhoneNo] = useState("");
  const [textQuestionAnswers, setTextQuestionAnswers] = useState([]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    referralCode: refParam || "",
    password: "",
    avatar: "",
    dateOfBirth: "",
    highSchoolName: "",
    fishingTeamName: "",
  });

  const [multiChoiceQuestions, setMultiChoiceQuestions] = useState([]);
  const [textQuestions, setTextQuestions] = useState([]);
  const [referralCodes, setReferralCodes] = useState([]);
  const [filteredReferralCodes, setFilteredReferralCodes] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const questionsData = await getAllOwnerSelectionQuestions();
        const questionsArray = Object.values(questionsData);
        setMultiChoiceQuestions(questionsArray);

        const textQuestionsData = await getAllOwnerTextQuestions();
        const testQuestionsArray = Object.values(textQuestionsData);

        const filteredQuestions = testQuestionsArray.filter(
          (question) =>
            !isTaxIdentifierQuestion(question) &&
            !question.trim().toLowerCase().includes("address line 2")
        );

        setTextQuestions(filteredQuestions);
      } catch (error) {
        console.error("Error fetching Questions:", error);
      }
    };

    const fetchReferralCodes = async () => {
      try {
        const codes = await getAllReferralCodesWithOwners();
        setReferralCodes(codes);
        setFilteredReferralCodes(codes);
      } catch (error) {
        console.error("Error fetching referral codes:", error);
      }
    };

    fetchQuestions();
    fetchReferralCodes();
  }, []);

  const handleChange = (e, question) => {
    const { name, value } = e.target;

    // Clear validation error for this field when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }

    if (name.startsWith("question_")) {
      // Update textfield answers
      const questionIndex = parseInt(name.split("_")[1], 10);
      const existingAnswerIndex = textQuestionAnswers.findIndex(
        (answer) => answer.questionText === textQuestions[questionIndex]
      );

      if (existingAnswerIndex !== -1) {
        setTextQuestionAnswers((prevAnswers) => {
          const updatedAnswers = [...prevAnswers];
          updatedAnswers[existingAnswerIndex] = {
            questionText: textQuestions[questionIndex],
            answer: value,
          };
          return updatedAnswers;
        });
      } else {
        setTextQuestionAnswers((prevAnswers) => [
          ...prevAnswers,
          {
            questionText: textQuestions[questionIndex],
            answer: value,
          },
        ]);
      }
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
    }
    console.log(textQuestionAnswers);
  };

  const handleReferralSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    // Update the form data with the current input value
    setFormData({ ...formData, referralCode: value });

    // Filter referral codes based on search term
    const filtered = referralCodes.filter(
      (item) =>
        item.code.toLowerCase().includes(value.toLowerCase()) ||
        item.ownerName.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredReferralCodes(filtered);
    setShowDropdown(value.length > 0 && filtered.length > 0);
  };

  const handleReferralSelect = (selectedCode) => {
    setFormData({ ...formData, referralCode: selectedCode.code });
    setSearchTerm(selectedCode.code);
    setShowDropdown(false);
  };

  const handleReferralFocus = () => {
    if (searchTerm.length > 0 && filteredReferralCodes.length > 0) {
      setShowDropdown(true);
    }
  };

  const handleReferralBlur = () => {
    // Delay hiding dropdown to allow clicks on dropdown items
    setTimeout(() => setShowDropdown(false), 200);
  };

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setIsSigningUp(true);
      setError("");
      setLoading(true);

      const params = new URLSearchParams(window.location.search);
      const redirectTo = params.get("redirect_to");

      const userCredential = await signup(formData.email, formData.password);
      const userId = userCredential.user.uid;

      const platformSettings = await getPlatformPercentages();
      const userSuffixNew = parseInt(platformSettings.userSuffix) + 1;
      const bbrID = "BBR" + userSuffixNew;

      const setLastSuffix = ref(getDatabase(), `portalSettings/settingsID`);
      await update(setLastSuffix, {
        userSuffix: userSuffixNew,
      });

      console.log("bbrID");
      console.log(bbrID);
      console.log("Referral Code being saved:", formData.referralCode);

      const userRef = ref(getDatabase(), `users/${userId}`);
      await set(userRef, {
        bbrId: bbrID,
        firstName: formData.firstName,
        lastName: formData.lastName,
        avatar: formData.avatar,
        refferalCode: (() => {
          const raw = (formData.referralCode || "").trim();
          if (!raw) return "";
          const matchedCode = referralCodes.find(
            (code) =>
              code.code.toUpperCase() === raw.toUpperCase() ||
              code.displayText === raw
          );
          return matchedCode ? matchedCode.code : raw.toUpperCase();
        })(),
        email: formData.email,
        phone: phoneNo,
        role: "Boat Owner",
        status: "Active",
        highSchoolName: formData.highSchoolName,
        fishingTeamName: formData.fishingTeamName,
      });
      const ownerMetaRef = ref(getDatabase(), `ownerMeta/${userId}`);

      await set(ownerMetaRef, {
        payments: {
          currentAmount: 0,
          totalAmount: 0,
        },
        totalPaidBookings: 0,
      });

      const userAge = calculateAgeFromDateOfBirth(formData.dateOfBirth);
      const userMetaRef = ref(getDatabase(), `userMeta/${userId}`);
      await set(userMetaRef, {
        dateOfBirth: formData.dateOfBirth,
        age: userAge,
        userAnswers: {
          multiChoiceQuestions: multiChoiceQuestions.map((question, index) => ({
            question: question.questionText,
            answer: selectedOptions[index]?.option || "",
          })),
          textQuestions: textQuestions.map((question, index) => ({
            question: question,
            answer: textQuestionAnswers[index]?.answer || "",
          })),
        },
      });
      ///////////
      const pathToBecomeProQuestions =
        "portalSettings/settingsID/defaultProQuestions/questions";
      // Get a reference to the selectionQuestions path
      const selectionQuestionsRef = ref(database, pathToBecomeProQuestions);

      // Retrieve data once from the database
      const snapshot = await get(selectionQuestionsRef);

      // Check if data exists
      let selectionQuestionsArray = [];
      if (snapshot.exists()) {
        // Convert the data to an array
        const selectionQuestionsData = snapshot.val();
        selectionQuestionsArray = Object.values(selectionQuestionsData);
      }

      // Store the questions data for later use if needed
      const questionsData = selectionQuestionsArray;
      // Create a pro staff request and automatically approve it
      const submissionRef = push(
        ref(database, `ownerMeta/${userId}/becomeProRequests`)
      );
      const timestamp = new Date().toISOString();

      // Convert formData to array format for compatibility with existing code
      const formDataArray = [
        {
          question: "Why do you want to be a Pro Staff Member?",
          answer: "Auto-approved through referral program signup"
        },
        {
          question: "What experience do you have with boating?",
          answer: formData.fishingTeamName ? `Fishing team: ${formData.fishingTeamName}` : "Experience through referral program"
        },
        {
          question: "How did you hear about BBR?",
          answer: formData.referralCode ? `Referral code: ${formData.referralCode}` : "Direct signup"
        }
      ];

      await set(submissionRef, { formData: formDataArray, timestamp });

      // Automatically approve as pro staff member
      await update(ref(database, `ownerMeta/${userId}`), {
        proStaffStatus: "Approved",
      });

      // Generate and save referral code for the pro staff member
      const referCode = `${formData.firstName}${formData.lastName}`.replace(
        /\s+/g,
        ""
      );
      const referralCodesRef = ref(database, `refferalCodes`);
      await push(referralCodesRef, { code: referCode, userId: userId });

      // const updatedOwnerMetaData = await getOwnerMeta(userId);
      // setOwnerMeta(updatedOwnerMetaData);
      ///////////////
      const userFullName = formData.firstName + " " + formData.lastName;
      const subject = "Welcome to BBR Referral Program! " + userFullName;
      const to = formData.email;

      const emailBodyNew = await welcomeOwnerEmailBody(userFullName);
      const emailSent = await sendEmailWithRetry(to, subject, emailBodyNew);

      if (!emailSent) {
        console.error("Failed to send welcome email to:", to);
        setError("Welcome email could not be sent. Please check your email address and try again.");
        return;
      } else {
        console.log("Welcome email sent successfully to:", to);
      }

      const smsBody = `Welcome to BBR Referral Program ${userFullName}! Your account has been registered successfully. We'll review your application and get back to you soon.\n\n${process.env.REACT_APP_BASE_URL}/auth`;

      // Send SMS (non-blocking - failure won't prevent signup)
      const smsResponse = await sendSMS(formData.phone, smsBody);
      if (smsResponse.success) {
        console.log("SMS sent successfully:", smsResponse.data);
      } else {
        console.log("SMS not sent:", smsResponse.message);
      }

      // Send admin notification about new user
      const adminEmailBody = await becomeProMemberRequestAdminEmailBody(
        userFullName
      );
      const adminEmailSent = await sendEmail(
        "waivers@bbrboatrentals.com",
        "Pro Member Application",
        adminEmailBody
      );

      if (!adminEmailSent) {
        console.error("Failed to send admin notification email");
      } else {
        console.log("Admin notification email sent successfully");
      }

      notifyNewBoatOwner(userFullName).catch((err) =>
        console.error("Failed to create admin notification:", err)
      );

      // Send pro staff approval notification to the user using proMemberStatusBody template
      const proStaffUserEmailBody = await proMemberStatusBody(userFullName, "Approved", referCode);

      const proStaffEmailSent = await sendEmail(
        to,
        "Congratulations! You're a BBR Pro Staff Member",
        proStaffUserEmailBody
      );

      if (!proStaffEmailSent) {
        console.error("Failed to send pro staff approval email to:", to);
      } else {
        console.log("Pro staff approval email sent successfully to:", to);
      }

      await refreshUserData();

      if (redirectTo) {
        if (redirectTo.startsWith('/')) {
          navigate(redirectTo);
        } else {
          navigate(`/listing/${redirectTo}`);
        }
      } else {
        // Redirect referral program signups to the pro staff page
        navigate("/dashboard/pro-staff");
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setIsSigningUp(false);
      setLoading(false);
    }
  }

  const [avatar, setAvatar] = useState(null);

  const onCrop = () => {
    const cropper = cropperRef.current.cropper;
    setCroppedImage(cropper.getCroppedCanvas().toDataURL());
  };

  const handleUploadAvatar = async () => {
    if (croppedImage) {
      // Convert the base64 image to a Blob
      const blob = await fetch(croppedImage).then((res) => res.blob());
      console.log(blob);
      // Create FormData and append the file
      const formData = new FormData();
      formData.append("file", blob);

      try {
        const response = await fetch(`${serverUrl}/upload-featured-image`, {
          method: "POST",
          body: formData,
        });

        if (response.ok) {
          const responseData = await response.json();
          setFormData((prevUserValues) => ({
            ...prevUserValues,
            avatar: `${serverUrl}/images/${responseData.file_name}`,
          }));
          setAvatar(null);
        } else {
          console.error("File upload failed");
        }
      } catch (error) {
        console.error("Error uploading file:", error);
      }
    } else {
      console.error("No cropped image available");
    }
  };

  const handleSelectImage = (e) => {
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onload = () => {
      setAvatar(reader.result); // Set the file URL as the avatar
    };
    reader.readAsDataURL(file); // Convert the file to a data URL
  };

  const nextStep = () => {
    if (step === 1) {
      if (validateStep1()) {
        setStep((prevStep) => prevStep + 1);
        // Clear validation errors when moving to next step
        setValidationErrors({});
      }
    }
  };

  const prevStep = () => {
    setStep((prevStep) => prevStep - 1);
    // Clear validation errors when going back to show clean form
    setValidationErrors({});
  };

  const [selectedOptions, setSelectedOptions] = useState([]);

  const handleRadioChange = (questionText, option) => {
    // Check if an option for the same question already exists
    const existingOptionIndex = selectedOptions.findIndex(
      (selectedOption) => selectedOption.questionText === questionText
    );

    if (existingOptionIndex !== -1) {
      // If an option exists, update the existing option
      setSelectedOptions((prevOptions) => {
        const updatedOptions = [...prevOptions];
        updatedOptions[existingOptionIndex] = {
          questionText,
          option,
        };
        return updatedOptions;
      });
    } else {
      // If no option exists, add a new option to the array
      setSelectedOptions((prevOptions) => [
        ...prevOptions,
        {
          questionText,
          option,
        },
      ]);
    }
  };

  const [redirectTo, setRedirectTo] = useState("");
  const [redirectId, setRedirectId] = useState("");
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get("redirect_to");
    if (redirectTo) {
      setRedirectTo(`?redirect_to=${redirectTo}`);
      setRedirectId(redirectTo);
    }
  }, []);

  const [showPassword, setShowPassword] = useState(false);

  // Add validation state
  const [validationErrors, setValidationErrors] = useState({});

  // Validation function for step 1
  const validateStep1 = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = "School team abbreviation is required";
    } else if (formData.firstName.trim().length > 6) {
      errors.firstName =
        "School team abbreviation must be 6 characters or less";
    }

    if (!formData.highSchoolName.trim()) {
      errors.highSchoolName = "school name is required";
    }

    if (!formData.fishingTeamName.trim()) {
      errors.fishingTeamName = "Fishing team name is required";
    }

    const age = calculateAgeFromDateOfBirth(formData.dateOfBirth);
    if (!formData.dateOfBirth || age === null || age < 21) {
      errors.dateOfBirth = "Date of birth is required and you must be 21 or older";
    }

    if (!phoneNo.trim()) {
      errors.phone = "Phone number is required";
    }

    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Please enter a valid email address";
    }

    if (!formData.password.trim()) {
      errors.password = "Password is required";
    } else if (formData.password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    if (!formData.lastName.trim()) {
      errors.lastName = "Please select an option";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Validation function for step 2
  const validateStep2 = () => {
    const errors = {};

    textQuestions.forEach((question, index) => {
      // Skip Address Line 2 as requested
      if (question.trim().toLowerCase() !== "address line 2") {
        const answer = textQuestionAnswers.find(
          (answer) => answer.questionText === question
        );
        if (!answer || !answer.answer.trim()) {
          errors[`question_${index}`] = "This field is required";
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  return (
    <>
      {/* Enhanced Header Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #deb568 0%, #deb568 100%)",
          padding: "2rem",
          borderRadius: "15px",
          marginBottom: "2rem",
          boxShadow: "0 10px 30px rgba(222, 181, 104, 0.3)",
          textAlign: "left",
        }}
      >
        <h1
          style={{
            color: "#000000",
            fontSize: "2.5rem",
            fontWeight: "bold",
            marginBottom: "0.5rem",
            fontFamily: '"Fjalla One", sans-serif',
          }}
        >
          BBR Referral Program
        </h1>
        <p
          style={{
            color: "#424242",
            fontSize: "1.2rem",
            marginBottom: "0",
            fontFamily: '"Poppins", sans-serif',
            fontWeight: "500",
          }}
        >
          Join our referral program and start earning money for your team!
        </p>
      </div>

      <p
        className="auth-description"
        style={{
          textAlign: "left",
          fontSize: "1.1rem",
          color: "#424242",
          marginBottom: "2rem",
          fontFamily: '"Poppins", sans-serif',
        }}
      >
        Please enter your credentials to create your referral program account.
        <br />
        Already have an account?{" "}
        <Link
          to={`/auth${redirectTo}`}
          style={{ color: "#deb568", fontWeight: "600" }}
        >
          Sign In
        </Link>
      </p>

      <div
        className="auth-credentials m-b-xxl"
        style={{
          background: "#ffffff",
          borderRadius: "20px",
          padding: "2rem",
          boxShadow: "0 15px 35px rgba(0,0,0,0.1)",
          border: "1px solid #c8c8c8",
        }}
      >
        <Row>
          <Col>
            <ProgressBar
              now={(step / 2) * 100}
              className="mb-4"
              style={{
                height: "8px",
                borderRadius: "10px",
                backgroundColor: "#c8c8c8",
              }}
            />
            <Form onSubmit={handleSubmit}>
              {error && (
                <Alert variant="danger" style={{ borderRadius: "10px" }}>
                  {error}
                </Alert>
              )}

              {step === 1 && (
                <>
                  <Form.Group controlId="firstName" className="mt-4">
                    <Row className="mb-3">
                      <Col xs={12}>
                        <Form.Label
                          className="form-label"
                          style={{ fontWeight: "600", color: "#000000" }}
                        >
                          Choose your school team abbreviation up to 6
                          characters
                          <span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          className="form-control"
                          style={{
                            borderRadius: "10px",
                            border: validationErrors.firstName
                              ? "2px solid #dc3545"
                              : "2px solid #c8c8c8",
                          }}
                          placeholder="e.g. TWCP"
                          maxLength={6}
                          required
                        />
                        {validationErrors.firstName && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.firstName}
                          </div>
                        )}
                      </Col>
                    </Row>

                    <Form.Group className="mb-3" controlId="lastName">
                      <Form.Label
                        style={{ fontWeight: "600", color: "#000000" }}
                      >
                        Choose if you are a COLLEGE or HIGHSCHOOL
                        <span style={{ color: "red" }}> *</span>
                      </Form.Label>
                      <Form.Select
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        className="form-control"
                        style={{
                          borderRadius: "10px",
                          border: validationErrors.lastName
                            ? "2px solid #dc3545"
                            : "2px solid #ced4da",
                          padding: "12px",
                          fontSize: "1rem",
                          lineHeight: "1.5",
                        }}
                        required
                      >
                        <option value="">Select an option</option>
                        <option value="HIGHSCHOOL">HIGHSCHOOL</option>
                        <option value="COLLEGE">COLLEGE</option>
                      </Form.Select>
                      {validationErrors.lastName && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {validationErrors.lastName}
                        </div>
                      )}
                    </Form.Group>

                    <Row className="mb-3">
                      <Col xs={6}>
                        <Form.Label
                          style={{ fontWeight: "600", color: "#000000" }}
                        >
                          Name of your school
                          <span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="highSchoolName"
                          value={formData.highSchoolName}
                          onChange={handleChange}
                          className="form-control"
                          style={{
                            borderRadius: "10px",
                            border: validationErrors.highSchoolName
                              ? "2px solid #dc3545"
                              : "2px solid #c8c8c8",
                          }}
                          placeholder="e.g. Riddell High School"
                          required
                        />
                        {validationErrors.highSchoolName && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.highSchoolName}
                          </div>
                        )}
                      </Col>
                      <Col xs={6}>
                        <Form.Label
                          style={{ fontWeight: "600", color: "#000000" }}
                        >
                          Name of your fishing team
                          <span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="fishingTeamName"
                          value={formData.fishingTeamName}
                          onChange={handleChange}
                          className="form-control"
                          style={{
                            borderRadius: "10px",
                            border: validationErrors.fishingTeamName
                              ? "2px solid #dc3545"
                              : "2px solid #c8c8c8",
                          }}
                          placeholder="e.g. Riddell Bass Dawgs Fishing Team"
                          required
                        />
                        {validationErrors.fishingTeamName && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.fishingTeamName}
                          </div>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col xs={12}>
                        <Form.Label
                          className="form-label"
                          style={{ fontWeight: "600", color: "#000000" }}
                        >
                          Profile Picture{" "}
                          <span style={{ color: "#424242", fontSize: "0.9em" }}>
                            (optional)
                          </span>
                        </Form.Label>
                        <Form.Control
                          type="file"
                          name="avatar"
                          onChange={(e) => handleSelectImage(e)}
                          accept="image/*"
                          style={{
                            borderRadius: "10px",
                            border: "2px solid #c8c8c8",
                          }}
                        />
                        {avatar && (
                          <>
                            <Cropper
                              src={avatar} // Image source
                              className="mt-3"
                              style={{
                                height: 400,
                                width: "100%",
                                borderRadius: "10px",
                              }} // Styling for Cropper container
                              initialAspectRatio={16 / 9} // Initial aspect ratio for cropping
                              guides={false} // Display cropping guides
                              crop={onCrop} // Callback function triggered after cropping
                              ref={cropperRef} // Assign the ref to the Cropper component
                            />
                            <Button
                              className="mt-3"
                              onClick={handleUploadAvatar}
                              style={{
                                backgroundColor: "#deb568",
                                borderColor: "#deb568",
                                borderRadius: "10px",
                                fontWeight: "600",
                              }}
                            >
                              Finish Cropping
                            </Button>
                          </>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col xs={6}>
                        <Form.Label
                          style={{ fontWeight: "600", color: "#000000" }}
                        >
                          Date of Birth<span style={{ color: "red" }}> *</span>
                          <span>(must be 21 or older)</span>
                        </Form.Label>
                        <Form.Control
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          className="form-control"
                          onChange={handleChange}
                          max={new Date().toISOString().split("T")[0]}
                          style={{
                            borderRadius: "10px",
                            border: validationErrors.dateOfBirth
                              ? "2px solid #dc3545"
                              : "2px solid #c8c8c8",
                          }}
                          required
                        />
                        {validationErrors.dateOfBirth && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.dateOfBirth}
                          </div>
                        )}
                      </Col>
                      <Col xs={6}>
                        <Form.Label
                          style={{ fontWeight: "600", color: "#000000" }}
                        >
                          Phone Number <span>(Your Fishing Team Admin)</span>
                          <span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <PhoneInput
                          country={"us"}
                          value={phoneNo}
                          placeholder="e.g. 123-456-7890"
                          onChange={(value) => setPhoneNo(value)}
                          inputStyle={{
                            width: "100%",
                            height: "38px",
                            border: validationErrors.phone
                              ? "2px solid #dc3545"
                              : "2px solid #c8c8c8",
                            borderRadius: "10px",
                          }}
                        />
                        {validationErrors.phone && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.phone}
                          </div>
                        )}
                      </Col>
                    </Row>

                    <Row className="mb-3">
                      <Col xs={6}>
                        <Form.Label
                          style={{ fontWeight: "600", color: "#000000" }}
                        >
                          Email (Your Fishing Team Admin)
                          <span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          placeholder="admin@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          className="form-control"
                          style={{
                            borderRadius: "10px",
                            border: validationErrors.email
                              ? "2px solid #dc3545"
                              : "2px solid #c8c8c8",
                          }}
                          required
                        />
                        {validationErrors.email && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.email}
                          </div>
                        )}
                      </Col>
                      <Col xs={6}>
                        <Form.Label
                          style={{ fontWeight: "600", color: "#000000" }}
                        >
                          Password<span style={{ color: "red" }}> *</span>
                        </Form.Label>
                        <div style={{ position: "relative", width: "100%" }}>
                          <Form.Control
                            type={showPassword ? "text" : "password"}
                            placeholder="********"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className="form-control"
                            required
                            style={{
                              paddingRight: "2.5rem",
                              borderRadius: "10px",
                              border: validationErrors.password
                                ? "2px solid #dc3545"
                                : "2px solid #c8c8c8",
                            }}
                          />
                          <button
                            type="button"
                            aria-label={
                              showPassword ? "Hide password" : "Show password"
                            }
                            onClick={() => setShowPassword((prev) => !prev)}
                            style={{
                              position: "absolute",
                              right: "0.5rem",
                              top: "50%",
                              transform: "translateY(-50%)",
                              background: "none",
                              border: "none",
                              cursor: "pointer",
                              padding: 0,
                              height: "2rem",
                              width: "2rem",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {showPassword ? (
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M17.94 17.94A10.94 10.94 0 0 1 12 19c-5 0-9.27-3.11-11-7 1.21-2.61 3.31-4.77 6-6.13" />
                                <path d="M1 1l22 22" />
                                <path d="M9.53 9.53A3.5 3.5 0 0 0 12 16.5c1.38 0 2.63-.56 3.54-1.47" />
                                <path d="M14.47 14.47A3.5 3.5 0 0 1 12 7.5c-.88 0-1.68.32-2.32.84" />
                              </svg>
                            ) : (
                              <svg
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            )}
                          </button>
                        </div>
                        {validationErrors.password && (
                          <div
                            style={{
                              color: "#dc3545",
                              fontSize: "0.875rem",
                              marginTop: "0.25rem",
                            }}
                          >
                            {validationErrors.password}
                          </div>
                        )}
                      </Col>
                    </Row>
                    {/* Referral Code field removed as requested */}
                  </Form.Group>

                  <Row className="mt-4">
                    <Col>
                      <Button
                        variant="btn btn-primary"
                        onClick={nextStep}
                        style={{
                          backgroundColor: "#deb568",
                          borderColor: "#deb568",
                          borderRadius: "10px",
                          fontWeight: "600",
                          padding: "12px 30px",
                        }}
                      >
                        Next Step →
                      </Button>
                    </Col>
                  </Row>
                </>
              )}
              {step === 2 && (
                <>
                  {textQuestions.map((question, questionIndex) => (
                    <Form.Group key={questionIndex} className="mt-4">
                      <Form.Label
                        style={{ fontWeight: "600", color: "#000000" }}
                      >
                        {question}
                        <span style={{ color: "red" }}> *</span>
                      </Form.Label>
                      <Form.Control
                        rows={3}
                        name={`question_${questionIndex}`}
                        value={
                          textQuestionAnswers.find(
                            (answer) => answer.questionText === question
                          )?.answer || ""
                        }
                        onChange={(e) => handleChange(e, question)}
                        required={true}
                        style={{
                          borderRadius: "10px",
                          border: validationErrors[`question_${questionIndex}`]
                            ? "2px solid #dc3545"
                            : "2px solid #c8c8c8",
                        }}
                      />
                      {validationErrors[`question_${questionIndex}`] && (
                        <div
                          style={{
                            color: "#dc3545",
                            fontSize: "0.875rem",
                            marginTop: "0.25rem",
                          }}
                        >
                          {validationErrors[`question_${questionIndex}`]}
                        </div>
                      )}
                    </Form.Group>
                  ))}
                  <Row>
                    <Col>
                      <Form.Check
                        type="checkbox"
                        style={{ marginTop: "20px" }}
                        label={
                          <>
                            By signing up for BBR Referral Program, users agree
                            to abide by these
                            <span
                              onClick={openModal}
                              style={{
                                marginLeft: "5px",
                                marginRight: "5px",
                                textDecoration: "underline",
                                color: "#deb568",
                                cursor: "pointer",
                                fontWeight: "600",
                              }}
                            >
                              rules
                            </span>
                            and contribute to a safe, fair, and trusted
                            community of referral program participants.
                          </>
                        }
                        required
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <div className="auth-submit mt-4">
                        <Button
                          className="mx-2 mb-2"
                          variant="secondary"
                          onClick={prevStep}
                          style={{
                            borderRadius: "10px",
                            fontWeight: "600",
                            padding: "12px 30px",
                          }}
                        >
                          ← Previous
                        </Button>
                        <Button
                          className="mx-2"
                          variant="primary"
                          type="submit"
                          disabled={loading}
                          onClick={(e) => {
                            if (!validateStep2()) {
                              e.preventDefault();
                            }
                          }}
                          style={{
                            backgroundColor: "#deb568",
                            borderColor: "#deb568",
                            borderRadius: "10px",
                            fontWeight: "600",
                            padding: "12px 30px",
                            fontSize: "1.1rem",
                          }}
                        >
                          {loading
                            ? "Creating Account..."
                            : "Join Referral Program"}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </Form>
          </Col>
        </Row>

        <Modal centered show={isModalOpen} onHide={closeModal}>
          <Modal.Header
            closeButton
            style={{ backgroundColor: "#deb568", color: "#ffffff" }}
          >
            <Modal.Title style={{ fontWeight: "bold" }}>
              BBR Referral Program - Rules and Guidelines
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ fontFamily: '"Poppins", sans-serif' }}>
            <h4 style={{ color: "#deb568", fontWeight: "600" }}>
              1. Communication Within the BBR Platform Only
            </h4>
            <p>
              All communication between referral program participants and BBR
              must take place exclusively within the BBR Bass Boat Rentals
              dashboard. Sharing personal phone numbers, emails, or any other
              form of contact information for the purpose of arranging
              transactions outside of the system is strictly prohibited.
            </p>
            <h5 style={{ color: "#000000", fontWeight: "600" }}>Reason:</h5>
            <ul>
              <li>
                <strong>Fair Business Practices:</strong> BBR's platform is
                designed to ensure that transactions and referrals are
                transparent, secure, and fairly conducted. Making transactions
                outside of the system compromises the fairness and integrity of
                the platform.
              </li>
              <li>
                <strong>Safety in Communications:</strong> By keeping all
                communication within the dashboard, BBR can ensure that any
                issues or disputes can be resolved fairly. It also protects the
                privacy of all parties by keeping personal contact details
                confidential.
              </li>
              <li>
                <strong>Liability Protection:</strong> BBR provides legal
                coverage and waivers to all participants through the platform.
                Any transaction or communication outside of the system could
                lead to liability issues and will not be covered under BBR's
                policies.
              </li>
            </ul>
            <h4 style={{ color: "#deb568", fontWeight: "600" }}>
              2. No Off-Platform Transactions
            </h4>
            <p>
              All referrals, payments, and transactions must be completed
              through the BBR Bass Boat Rentals system. Any attempt to
              circumvent this process by making off-platform deals or payments
              will result in suspension or termination of the account.
            </p>
            <h5 style={{ color: "#000000", fontWeight: "600" }}>Reason:</h5>
            <ul>
              <li>
                This ensures that the platform can properly track, secure, and
                guarantee each referral transaction.
              </li>
              <li>
                Off-platform transactions undermine the protection offered by
                BBR's system, including payment security, insurance, and
                liability coverage.
              </li>
            </ul>
            <h4 style={{ color: "#deb568", fontWeight: "600" }}>
              3. Adherence to BBR's Safety Standards
            </h4>
            <p>
              By signing up, users agree to adhere to the safety guidelines and
              standards set by BBR Bass Boat Rentals, which include following
              local boating laws and regulations, providing accurate information
              about referrals, and maintaining ethical business practices.
            </p>
            <h4 style={{ color: "#deb568", fontWeight: "600" }}>
              4. Liability Waivers
            </h4>
            <p>
              All users must acknowledge that BBR Bass Boat Rentals provides
              liability waivers to all participants. These waivers are only
              valid for transactions made through the platform. BBR cannot
              guarantee or cover any damages, losses, or issues arising from
              transactions made outside of the platform.
            </p>
            <h5 style={{ color: "#000000", fontWeight: "600" }}>Reason:</h5>
            <ul>
              <li>
                The liability waivers ensure that all parties are legally
                protected during the referral process, but this protection is
                only available for on-platform transactions.
              </li>
            </ul>
            <h4 style={{ color: "#deb568", fontWeight: "600" }}>
              5. Community of Trust
            </h4>
            <p>
              BBR Bass Boat Rentals strives to create a community built on trust
              and mutual respect. By following these rules, users contribute to
              a safe and reliable environment for everyone involved. Maintaining
              the integrity of the platform helps referral participants feel
              secure about their transactions, and it assures BBR that they are
              working with verified and trustworthy participants.
            </p>
            <h4 style={{ color: "#deb568", fontWeight: "600" }}>
              6. Violations of Terms
            </h4>
            <p>
              Any violation of these rules, including attempts to circumvent the
              platform, will result in immediate action, which may include
              suspension or permanent removal from the platform.
            </p>
            <p
              style={{
                marginTop: "20px",
                padding: "15px",
                backgroundColor: "#c8c8c8",
                borderRadius: "10px",
              }}
            >
              <strong>
                By signing up for BBR Referral Program, users agree to abide by
                these rules and contribute to a safe, fair, and trusted
                community of referral program participants.
              </strong>
            </p>
          </Modal.Body>
        </Modal>
      </div>
    </>
  );
};

export default SignupReferral;
